import React, { useState, useEffect } from 'react';
import axios from 'axios';

const API_BASE_URL =
  process.env.NODE_ENV === 'production'
    ? process.env.REACT_APP_API_URL_PRODUCTION
    : process.env.REACT_APP_API_URL;

const ImageCategory = ({ onClose, showToast, refreshCategories, categories: initialCategories }) => {
  const [categories, setCategories] = useState([]);
  const [images, setImages] = useState([]);
  const [form, setForm] = useState('');
  const [editingId, setEditingId] = useState(null);
  // Removed local 'toast' state, will use showToast prop
  const [activeCategoryId, setActiveCategoryId] = useState(null);
  const [imageModalOpen, setImageModalOpen] = useState(false);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [deleteConfirmId, setDeleteConfirmId] = useState(null);
  // NEW: State for category search within this modal
  const [categorySearch, setCategorySearch] = useState('');

  useEffect(() => {
    setCategories(initialCategories);
    fetchImages();
  }, [initialCategories]);

  const fetchImages = async () => {
    try {
      const res = await axios.get(`${API_BASE_URL}/api/images`);
      setImages(res.data || []);
    } catch (err) {
      console.error('Error fetching images:', err);
      showToast.error('Error fetching images.'); // Using prop toast
    }
  };

  const fetchCategories = async () => {
    try {
      const res = await axios.get(`${API_BASE_URL}/api/image-categories`);
      setCategories(res.data || []);
      refreshCategories?.();
    } catch (err) {
      console.error('Error fetching categories:', err);
      showToast.error('Error fetching categories.'); // Using prop toast
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingId) {
        await axios.patch(`${API_BASE_URL}/api/image-categories/${editingId}`, { name: form });
        showToast.success('✏️ Category updated'); // Using prop toast
      } else {
        await axios.post(`${API_BASE_URL}/api/image-categories`, { name: form });
        showToast.success('➕ Category created'); // Using prop toast
      }
      setForm('');
      setEditingId(null);
      fetchCategories();
    } catch (err) {
      console.error('Category submit error:', err);
      showToast.error('❌ Operation failed.'); // Using prop toast
    }
  };

  const confirmDelete = async () => {
    try {
      await axios.delete(`${API_BASE_URL}/api/image-categories/${deleteConfirmId}`);
      showToast.info('🗑️ Category deleted'); // Using prop toast
      if (activeCategoryId === deleteConfirmId) setActiveCategoryId(null);
      setDeleteConfirmId(null);
      fetchCategories();
    } catch (err) {
      console.error('Delete error:', err);
      showToast.error('❌ Delete failed.'); // Using prop toast
    }
  };

  const toggleImageInCategory = async (imageId, isInCategory) => {
    try {
      const endpoint = `${API_BASE_URL}/api/images/${imageId}`;
      const image = images.find((img) => img._id === imageId);

      const updatedCategories = isInCategory
        ? image.imageCategories
            .filter((cat) => cat._id !== activeCategoryId)
            .map((cat) => cat._id)
        : [...image.imageCategories.map((cat) => cat._id), activeCategoryId];

      await axios.patch(endpoint, { imageCategories: updatedCategories });
      fetchImages();
      showToast.info(isInCategory ? '🗑️ Image removed from category' : '➕ Image added to category'); // Using prop toast
    } catch (err) {
      console.error('Error toggling image category:', err);
      showToast.error('❌ Failed to update image category.'); // Using prop toast
    }
  };

  // Filter categories based on search input
  const filteredCategories = categories.filter(cat =>
    cat.name.toLowerCase().includes(categorySearch.toLowerCase())
  );

  return (
    <div className="image-category-wrapper">
      <button className="close-btn" onClick={onClose}>×</button>

      <form onSubmit={handleSubmit}>
        <input
          type="text"
          placeholder="New Category Name"
          value={form}
          onChange={(e) => setForm(e.target.value)}
          required
        />
        <button type="submit">{editingId ? 'Update' : 'Add'}</button>
      </form>

      {/* NEW: Category Search Bar */}
      <div className="category-search-container">
        <input
          type="search"
          placeholder="Search categories..."
          className="category-search-input"
          value={categorySearch}
          onChange={(e) => setCategorySearch(e.target.value)}
        />
      </div>

      <ul className="category-list-admin">
        {filteredCategories.length > 0 ? (
          filteredCategories.map((cat) => (
            <li key={cat._id} className={cat._id === activeCategoryId ? 'active' : ''}>
              <span onClick={() => setActiveCategoryId(cat._id)}>{cat.name}</span>
              <button onClick={() => {
                setForm(cat.name);
                setEditingId(cat._id);
              }}>✏️</button>
              <button onClick={() => setDeleteConfirmId(cat._id)}>🗑️</button>
            </li>
          ))
        ) : (
          <p className="no-categories-message">No categories found matching your search.</p>
        )}
      </ul>

      {deleteConfirmId && (
        <div className="modal-overlay" onClick={() => setDeleteConfirmId(null)}>
          <div className="modal-content delete" onClick={(e) => e.stopPropagation()}>
            <h2>Are you sure you want to delete this category?</h2>
            <div className="form-buttons">
              <button onClick={confirmDelete}>Yes, Delete</button>
              <button className="cancel-btn" onClick={() => setDeleteConfirmId(null)}>Cancel</button>
            </div>
          </div>
        </div>
      )}

      {activeCategoryId && (
        <div className="category-images">
          <button className="open-form-btn" onClick={() => setImageModalOpen(true)}>Add / Remove Images</button>
          <h4>Images in this Category</h4>
          {images
            .filter(img => img.imageCategories?.some(cat => cat._id === activeCategoryId))
            .map(img => (
              <div key={img._id} className="image-item">
                <img
                  src={img.url}
                  alt={img.name}
                  onClick={() => setPreviewUrl(img.url)}
                  style={{ cursor: 'pointer' }}
                />
                <div className="image-info">
                  <h5>{img.name}</h5>
                  <p>{img.description}</p>
                </div>
                <button className="remove-btn" onClick={() => toggleImageInCategory(img._id, true)}>🗑️</button>
              </div>
          ))}
          {images.filter(img => img.imageCategories?.some(cat => cat._id === activeCategoryId)).length === 0 && (
              <p className="no-images-message">No images assigned to this category yet.</p>
          )}
        </div>
      )}

      {imageModalOpen && (
        <div className="modal-overlay" onClick={() => setImageModalOpen(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h3>Select Images for "{categories.find(cat => cat._id === activeCategoryId)?.name}"</h3>
            <div className="image-select-list">
              {images.length > 0 ? (
                images.map((img) => {
                  const isInCategory = img.imageCategories?.some(cat => cat._id === activeCategoryId);
                  return (
                    <div key={img._id} className="image-item">
                      <img
                        src={img.url}
                        alt={img.name}
                        onClick={() => setPreviewUrl(img.url)}
                        style={{ cursor: 'pointer' }}
                      />
                      <div className="image-info">
                        <h5>{img.name}</h5>
                        <p>{img.description}</p>
                      </div>
                      <button
                        className={isInCategory ? 'remove-btn' : 'add-btn'}
                        onClick={() => toggleImageInCategory(img._id, isInCategory)}
                      >
                        {isInCategory ? 'Remove' : 'Add'}
                      </button>
                    </div>
                  );
                })
              ) : (
                <p>No images available to add or remove.</p>
              )}
            </div>
            <button className="close-btn" onClick={() => setImageModalOpen(false)}>×</button>
          </div>
        </div>
      )}

      {previewUrl && (
        <div className="image-preview-overlay" onClick={() => setPreviewUrl(null)}>
          <div className="preview-box" onClick={(e) => e.stopPropagation()}>
            <button className="close-btn" onClick={() => setPreviewUrl(null)}>×</button>
            <img src={previewUrl} alt="Preview" />
          </div>
        </div>
      )}

      {/* Removed local toast display as we are using the prop toast */}
    </div>
  );
};

export default ImageCategory;